// IWCF Kill Sheet — display rounding helpers (P2 of Kill Sheet Redev).
//
// The IWCF reference form rounds individual cells at display while
// continuing internal calculations against the unrounded values. These
// helpers centralize that rounding contract so the wizard UI and the PDF
// generator format every cell identically.
//
// Pure functions; no I/O; no state. Safe to import from React components.

const _isNum = (v) => Number.isFinite(v);

/**
 * Round a number to `d` fixed decimal places using deterministic half-up
 * rounding, with a tiny epsilon to compensate for IEEE-754 representation
 * artefacts (e.g. 1065 × 0.00981 stores as 10.4476499… instead of 10.44765,
 * which would otherwise round DOWN to 10.4476 instead of UP to 10.4477 —
 * the IWCF-printed value).  Returns a number, not a string.
 * Negative `d` is treated as 0.
 */
export function roundTo(value, d = 0) {
  if (!_isNum(value)) return null;
  const p = Math.pow(10, Math.max(0, d));
  const scaled = value * p;
  // Relative epsilon: large enough to nudge IEEE-754 boundary cases, small
  // enough to never affect a genuine half-down case.
  const eps = (Math.abs(scaled) + 1) * 1e-12;
  const adjusted = scaled + (scaled >= 0 ? eps : -eps);
  return Math.round(adjusted) / p;
}

/**
 * Format helpers — one per IWCF cell category. Decimal counts match what the
 * IWCF reference prints. NULL inputs render as "—" so partially-populated
 * sessions don't crash the printout.
 */
export const FMT = {
  density:   (v) => _isNum(v) ? roundTo(v, 2)   : null,   // ppg or kg/m³
  gradient:  (v) => _isNum(v) ? roundTo(v, 4)   : null,   // psi/ft or kPa/m
  pressure:  (v) => _isNum(v) ? roundTo(v, 0)   : null,   // psi or kPa (display whole numbers)
  pressure2: (v) => _isNum(v) ? roundTo(v, 2)   : null,   // 2-dp pressure (MAASP cells in IWCF)
  volume:    (v) => _isNum(v) ? roundTo(v, 2)   : null,   // bbl or m³ (2dp — subtotals)
  volume3:   (v) => _isNum(v) ? roundTo(v, 3)   : null,   // bbl or m³ (3dp — small section rows e.g. 0.625)
  capacity:  (v) => _isNum(v) ? roundTo(v, 5)   : null,   // bbl/ft or m³/m (5dp matches IWCF)
  strokes:   (v) => _isNum(v) ? Math.round(v)   : null,   // integer
  minutes:   (v) => _isNum(v) ? roundTo(v, 0)   : null,   // integer (IWCF rounds to whole min)
  fineMin:   (v) => _isNum(v) ? roundTo(v, 2)   : null,   // for sub-minute rows (e.g. surface line)
  kPer100:   (v) => _isNum(v) ? roundTo(v, 2)   : null,   // pressure per 100 strokes
  // Whole-number rounded volume that matches IWCF page 1 (it prints 33, 47, 48,
  // not 32.97, 47.46, 47.68).
  iwcfVolumeWhole: (v) => _isNum(v) ? Math.round(v) : null,
};

/**
 * Format a number with a fixed number of decimal places for use in printable
 * output. Uses epsilon-adjusted half-up rounding via `roundTo` so the result
 * matches the IWCF-printed values exactly even at IEEE-754 boundary cases.
 * Returns "—" for null / non-finite input.
 */
export function asFixed(value, d) {
  if (!_isNum(value)) return "—";
  return roundTo(value, d).toFixed(d);
}
